import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, IconButton, InputAdornment, Menu, MenuItem, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { ChevronDown, ChevronRight, MoreHorizontal, Plus, Play, CheckCheck, Search, Zap, ArrowRight, OctagonAlert, Flag, CornerDownRight } from "lucide-react";
import { GlassCard, T, TYPE, formatDate, primaryButtonSx, ghostButtonSx, labelSx } from "../../user/complaints/shared";
import { useSocketEvent } from "../../helpdesk/socket";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { TypeIcon, PriorityIcon, Assignee, StatusPill, Labels, Progress } from "../ui";
import { TypeSelect, fieldSx } from "../inputs";
import IssueDetail, { IssueDetailModal } from "../IssueDetail";
import { RequirementsDialog } from "./RequirementsDialog";
import { SprintFormDialog, CompleteSprintDialog, CancelSprintDialog } from "./SprintDialogs";
import { BulkBar } from "./BoardCanvas";

export const UNIT_LABEL = { storyPoints: "pts", originalEstimate: "h", remainingEstimate: "h left", hours: "h", days: "d", count: "issues", customField: "" };

/** Sub-tasks sit under their parent when both are in the same section. */
const nest = (issues) => {
  const byParent = new Map();
  const ids = new Set(issues.map((i) => String(i._id)));
  for (const i of issues) if (i.parentId && ids.has(String(i.parentId))) byParent.set(String(i.parentId), [...(byParent.get(String(i.parentId)) || []), i]);
  const out = [];
  for (const i of issues) {
    if (i.parentId && ids.has(String(i.parentId))) continue;
    out.push({ issue: i, depth: 0 });
    for (const s of byParent.get(String(i._id)) || []) out.push({ issue: s, depth: 1 });
  }
  return out;
};

const Row = ({ issue, depth, epic, unit, selected, onSelect, onOpen, draggable, onDragStart, action }) => (
  <Box data-row={issue._id} draggable={draggable} onDragStart={onDragStart} onClick={() => onOpen(issue)}
    sx={{ display: "flex", alignItems: "center", gap: 1, pl: 1.25 + depth * 3, pr: 1.25, py: 0.75, borderBottom: `1px solid ${T.border}`, bgcolor: selected ? T.brandSoft : "#fff", cursor: draggable ? "grab" : "pointer", "&:hover": { bgcolor: selected ? T.brandSoft : T.surfaceMuted } }}>
    <Checkbox size="small" checked={selected} onClick={(e) => e.stopPropagation()} onChange={(e) => onSelect(e.target.checked)} sx={{ p: 0.25 }} />
    {depth > 0 && <CornerDownRight size={13} color={T.inkMuted} />}
    <TypeIcon typeId={issue.typeId} size={14} />
    <Typography sx={{ fontSize: TYPE.small, fontWeight: 600, color: T.inkSecondary, minWidth: 72, textDecoration: issue.statusCategory === "done" ? "line-through" : "none" }}>{issue.key}</Typography>
    <Typography noWrap sx={{ fontSize: TYPE.small, flexGrow: 1, minWidth: 0 }}>{issue.summary}</Typography>
    {issue.blockedBy?.length > 0 && <Tooltip title={`Blocked by ${issue.blockedBy.map((b) => b.key).join(", ")}`}><OctagonAlert size={14} color="#B42318" /></Tooltip>}
    {issue.flagged && <Tooltip title={issue.flagReason || "Flagged"}><Flag size={14} color="#B54708" /></Tooltip>}
    {issue.subtasks?.total > 0 && <Chip size="small" label={`${issue.subtasks.done}/${issue.subtasks.total}`} sx={{ height: 18, fontSize: "0.62rem" }} />}
    {epic && <Chip size="small" label={epic.summary} sx={{ height: 18, fontSize: "0.64rem", maxWidth: 140, bgcolor: "#F4EBFF", color: "#6941C6", display: { xs: "none", md: "flex" } }} />}
    <Box sx={{ display: { xs: "none", md: "block" } }}><Labels labels={issue.labels} max={2} /></Box>
    <StatusPill statusId={issue.statusId} />
    {issue.estimate > 0 && <Tooltip title={`Estimate (${unit})`}><Box component="span" sx={{ minWidth: 24, px: 0.6, height: 20, borderRadius: "10px", bgcolor: T.track, fontSize: "0.7rem", fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{issue.estimate}</Box></Tooltip>}
    <PriorityIcon priorityId={issue.priorityId} size={15} />
    <Assignee person={issue.assignee} size={22} />
    {action}
  </Box>
);

const QuickCreate = ({ project, sprintId, onCreated }) => {
  const ctx = usePm();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState("");
  const [typeId, setTypeId] = useState(project.issueTypeIds.map(String).find((id) => ctx.typeById.get(id)?.key === "story") || project.issueTypeIds.map(String).find((id) => ctx.typeById.get(id)?.level === 1));
  const submit = async () => {
    if (!summary.trim()) return;
    try {
      await pm.createIssue(project._id, { summary, typeId, sprintId: sprintId || undefined });
      setSummary("");
      onCreated();
    } catch (err) {
      ctx.fail(err);
    }
  };
  if (!open) return <Button size="small" startIcon={<Plus size={14} />} onClick={() => setOpen(true)} sx={{ m: 0.5, color: T.inkSecondary }}>Create issue</Button>;
  return (
    <Stack direction="row" spacing={1} sx={{ p: 1 }} alignItems="center">
      <Box sx={{ width: 150 }}><TypeSelect label="" value={typeId} onChange={setTypeId} typeIds={project.issueTypeIds} level={[1]} /></Box>
      <TextField autoFocus fullWidth size="small" placeholder="What needs to be done? (Enter to create, Esc to close)" value={summary} onChange={(e) => setSummary(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") setOpen(false); }} sx={fieldSx} />
    </Stack>
  );
};

const BoardBacklog = ({ board, reloadBoard }) => {
  const ctx = usePm();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const [epicFilter, setEpicFilter] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [selected, setSelected] = useState([]);
  const [sprintForm, setSprintForm] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [menu, setMenu] = useState(null);
  const [moveMenu, setMoveMenu] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const [closedSprints, setClosedSprints] = useState([]);
  const project = board.project;
  const perms = board.permissions || [];
  const can = (p) => perms.includes(p) && !project.archivedAt;
  const unit = UNIT_LABEL[board.estimationConfig?.unit || "storyPoints"];
  const openKey = params.get("issue");

  const load = useCallback(async () => {
    try {
      const query = epicFilter ? { all: [{ field: "epic", op: "eq", value: epicFilter }] } : undefined;
      setData(await pm.boardBacklog(board._id, { text: text || undefined, ...(query ? { query: JSON.stringify(query) } : {}) }));
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [board._id, text, epicFilter]);
  useEffect(() => {
    const t = setTimeout(load, text ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, text]);
  useEffect(() => {
    if (board.type === "scrum") pm.boardSprints(board._id, { status: "completed,cancelled" }).then(setClosedSprints).catch(() => {});
  }, [board._id, board.type, data]);
  useSocketEvent(["issue.created", "issue.updated", "issue.deleted", "board.updated", "board.issue.moved", "sprint.started", "sprint.completed", "sprint.updated"], () => load());

  const openIssue = (i) => {
    const n = new URLSearchParams(params);
    n.set("issue", typeof i === "string" ? i : i.key);
    setParams(n);
  };
  const closeIssue = () => {
    const n = new URLSearchParams(params);
    n.delete("issue");
    setParams(n, { replace: true });
  };

  const drop = async (e, section) => {
    e.preventDefault();
    setOver(null);
    const issue = dragging;
    setDragging(null);
    if (!issue) return;
    const rows = [...e.currentTarget.querySelectorAll("[data-row]")];
    const ids = selected.includes(issue._id) ? selected : [issue._id];
    const before = rows.find((el) => !ids.includes(el.dataset.row) && e.clientY < el.getBoundingClientRect().top + el.getBoundingClientRect().height / 2);
    const others = section.issues.filter((i) => !ids.includes(i._id));
    const beforeId = before?.dataset.row;
    const afterId = beforeId ? others[others.findIndex((i) => i._id === beforeId) - 1]?._id : others[others.length - 1]?._id;
    const targetSprint = section.sprint?._id || null;
    const changesSprint = board.type === "scrum" && String(issue.sprintId || "") !== String(targetSprint || "");
    try {
      await pm.rankOnBoard(board._id, { issueIds: ids, beforeId, afterId, ...(changesSprint ? { sprintId: targetSprint } : {}) });
      setSelected([]);
      load();
    } catch (err) {
      ctx.fail(err, "Could not move");
    }
  };

  const moveSelected = async (sprintId) => {
    setMoveMenu(null);
    try {
      await pm.rankOnBoard(board._id, { issueIds: selected, sprintId });
      setSelected([]);
      load();
    } catch (err) {
      ctx.fail(err);
    }
  };

  // kanban: pull the issue from the backlog onto the board (goes through the workflow)
  const pullToBoard = async (issue) => {
    try {
      const r = await pm.moveCard(board._id, { issueId: issue._id, columnId: data.nextColumn._id, rev: issue.rev });
      if (r.warnings?.length) ctx.notify(r.warnings.join(" · "), "warning");
      else ctx.notify(`${issue.key} moved to ${data.nextColumn.name}`);
      load();
    } catch (err) {
      const res = err?.response?.data;
      ctx.notify(res?.message || errorMessage(err), res?.code === "WIP_CONFIRM" ? "warning" : "error");
    }
  };

  const loadMore = async (s) => {
    try {
      const r = await pm.boardBacklog(board._id, { section: s.key, offset: s.issues.length, text: text || undefined });
      const page = r.sections.find((x) => x.key === s.key);
      setData((d) => ({ ...d, sections: d.sections.map((x) => (x.key === s.key ? { ...x, issues: [...x.issues, ...page.issues], hasMore: page.hasMore } : x)) }));
    } catch (err) {
      ctx.fail(err);
    }
  };

  const epicById = useMemo(() => new Map((data?.epics || []).map((e) => [String(e._id), e])), [data]);
  if (error && !data) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>;
  if (data.type === "kanban" && !data.enabled) {
    return (
      <GlassCard sx={{ p: 3 }}>
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>The Kanban backlog is off for this board</Typography>
        <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mb: 2 }}>When on, issues in the first column ({board.columns[0]?.name}) leave the board and are prioritised here, then pulled onto the board when ready.</Typography>
        {board.canManage && <Button variant="contained" sx={primaryButtonSx} onClick={async () => { await pm.updateBoard(board._id, { backlogEnabled: true }); reloadBoard(); load(); }}>Enable Kanban backlog</Button>}
      </GlassCard>
    );
  }
  const hasActive = data.sections.some((s) => s.sprint?.status === "active");
  const openSprints = data.sections.filter((s) => s.sprint).map((s) => s.sprint);

  const section = (s) => {
    const sp = s.sprint;
    const key = s.key;
    const done = s.issues.filter((i) => i.statusCategory === "done").length;
    return (
      <GlassCard key={key} sx={{ mb: 2, overflow: "hidden" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 1, bgcolor: T.surfaceMuted, borderBottom: `1px solid ${T.border}` }} flexWrap="wrap" useFlexGap>
          <IconButton size="small" onClick={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))}>{collapsed[key] ? <ChevronRight size={16} /> : <ChevronDown size={16} />}</IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: TYPE.small }}>{sp ? sp.name : s.title}</Typography>
          {sp?.status === "active" && <Chip size="small" label="Active" color="success" sx={{ height: 20, fontSize: "0.66rem" }} />}
          {sp && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{sp.startDate ? `${formatDate(sp.startDate)} – ${formatDate(sp.endDate)}` : "Not scheduled"}</Typography>}
          <Box sx={{ flexGrow: 1 }} />
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{s.total} issues · {s.estimate} {unit}{sp ? ` · ${done} done` : ""}</Typography>
          {sp?.status === "future" && can("sprint.start") && ctx.me.isAdminRole && (
            <Tooltip title={hasActive && !project.allowParallelSprints ? "Complete the active sprint first" : ""}>
              <span><Button size="small" startIcon={<Play size={13} />} disabled={hasActive && !project.allowParallelSprints} onClick={() => setSprintForm({ mode: "start", sprint: sp })}>Start sprint</Button></span>
            </Tooltip>
          )}
          {sp?.status === "active" && can("sprint.complete") && ctx.me.isAdminRole && <Button size="small" startIcon={<CheckCheck size={13} />} onClick={() => setCompleting(sp)}>Complete sprint</Button>}
          {sp && (can("sprint.update") || can("sprint.create")) && <IconButton size="small" onClick={(e) => setMenu({ el: e.currentTarget, sprint: sp, count: s.total })}><MoreHorizontal size={15} /></IconButton>}
          {data.type === "kanban" && data.nextColumn && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Pull ready work onto the board → {data.nextColumn.name}</Typography>}
        </Stack>
        <Collapse in={!collapsed[key]}>
          {sp?.goal && <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary, px: 2, py: 0.75, borderBottom: `1px solid ${T.border}` }}>Goal: {sp.goal}</Typography>}
          <Box onDragOver={(e) => { if (can("issue.update") && dragging) { e.preventDefault(); setOver(key); } }} onDragLeave={() => setOver(null)} onDrop={(e) => drop(e, s)}
            sx={{ minHeight: 44, bgcolor: over === key ? T.brandSoft : "#fff", transition: "background .12s" }}>
            {!s.issues.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted, p: 1.5, textAlign: "center" }}>{sp ? "Drag issues here to plan this sprint" : "Nothing here"}</Typography>}
            {nest(s.issues).map(({ issue: i, depth }) => (
              <Row key={i._id} issue={i} depth={depth} unit={unit} epic={epicById.get(String(i.epicId))} draggable={can("issue.update") && board.ranking?.enabled !== false}
                selected={selected.includes(i._id)} onSelect={(on) => setSelected((x) => (on ? [...x, i._id] : x.filter((y) => y !== i._id)))} onOpen={openIssue}
                onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", i._id); setDragging(i); }}
                action={data.type === "kanban" && data.nextColumn && can("issue.transition") && (
                  <Tooltip title={`Move to ${data.nextColumn.name}`}><IconButton size="small" onClick={(e) => { e.stopPropagation(); pullToBoard(i); }}><ArrowRight size={15} /></IconButton></Tooltip>
                )} />
            ))}
            {s.hasMore && <Button fullWidth size="small" onClick={() => loadMore(s)}>Load more ({s.total - s.issues.length})</Button>}
          </Box>
          {can("issue.create") && <QuickCreate project={project} sprintId={sp?._id} onCreated={load} />}
        </Collapse>
      </GlassCard>
    );
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "240px minmax(0,1fr)" }, gap: 2 }}>
      <Box sx={{ display: { xs: "none", lg: "block" } }}>
        <GlassCard sx={{ p: 1.5, position: "sticky", top: 16 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}><Zap size={14} color="#7A5AF8" /><Typography sx={{ fontWeight: 700, fontSize: TYPE.small, ml: 0.75 }}>Epics</Typography></Stack>
          {[{ _id: null, summary: "All issues" }, { _id: "none", summary: "Without epic" }, ...(data.epics || [])].map((e) => (
            <Box key={String(e._id)} onClick={() => setEpicFilter(e._id)} sx={{ p: 0.75, borderRadius: "6px", cursor: "pointer", bgcolor: epicFilter === e._id ? T.brandSoft : "transparent", "&:hover": { bgcolor: T.surfaceMuted } }}>
              <Typography noWrap sx={{ fontSize: TYPE.small, fontWeight: epicFilter === e._id ? 700 : 500 }}>{e.key ? `${e.key} ` : ""}{e.summary}</Typography>
              {e.key && <Progress value={e.progressPct} height={4} />}
            </Box>
          ))}
        </GlassCard>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ sm: "center" }} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <TextField size="small" placeholder="Search backlog" value={text} onChange={(e) => setText(e.target.value)} sx={{ ...fieldSx, width: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search size={14} /></InputAdornment> }} />
          {selected.length > 0 && board.type === "scrum" && (
            <>
              <Button size="small" variant="outlined" sx={ghostButtonSx} onClick={(e) => setMoveMenu(e.currentTarget)}>Move {selected.length} to…</Button>
              <Menu anchorEl={moveMenu} open={Boolean(moveMenu)} onClose={() => setMoveMenu(null)}>
                <MenuItem onClick={() => moveSelected(null)}>Backlog</MenuItem>
                {openSprints.map((s) => <MenuItem key={s._id} onClick={() => moveSelected(s._id)}>{s.name}</MenuItem>)}
              </Menu>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {ctx.settings.ai?.enabled && perms.includes("ai.use") && can("issue.create") && <Button size="small" variant="outlined" sx={ghostButtonSx} onClick={() => setAiOpen(true)}>Requirements → issues</Button>}
          {board.type === "scrum" && can("sprint.create") && <Button size="small" variant="contained" sx={primaryButtonSx} startIcon={<Plus size={14} />} onClick={() => setSprintForm({ mode: "create" })}>Create sprint</Button>}
        </Stack>
        {selected.length > 0 && <BulkBar board={board} selected={selected} clear={() => setSelected([])} onDone={load} sprints={openSprints} columns={board.columns} />}
        {data.sections.map(section)}
        {board.type === "scrum" && closedSprints.length > 0 && (
          <GlassCard sx={{ p: 1.5 }}>
            <Typography sx={{ ...labelSx, mb: 1 }}>Completed & cancelled sprints</Typography>
            {closedSprints.slice(0, 10).map((s) => (
              <Stack key={s._id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.4 }}>
                <Typography sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{s.name}</Typography>
                <Chip size="small" label={s.status} sx={{ height: 18, fontSize: "0.62rem" }} />
                {s.status === "completed" && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{s.completed?.points ?? 0}/{s.committed?.points ?? 0} pts · {s.completed?.count ?? 0}/{s.committed?.count ?? 0} issues · {formatDate(s.completedAt)}</Typography>}
              </Stack>
            ))}
          </GlassCard>
        )}
        <Typography sx={{ ...labelSx, fontSize: "0.66rem", mt: 1 }}>Drag rows to rank them{board.type === "scrum" ? " or move them between the backlog and sprints" : ""}. Ranking is saved for everyone.</Typography>
      </Box>
      <Menu anchorEl={menu?.el} open={Boolean(menu)} onClose={() => setMenu(null)}>
        {can("sprint.update") && <MenuItem onClick={() => { setSprintForm({ mode: "edit", sprint: menu.sprint }); setMenu(null); }}>Edit sprint</MenuItem>}
        {can("sprint.create") && <MenuItem onClick={async () => { const s = menu.sprint; setMenu(null); try { await pm.duplicateSprint(s._id); ctx.notify(`${s.name} duplicated`); load(); } catch (err) { ctx.fail(err); } }}>Duplicate sprint</MenuItem>}
        {can("sprint.complete") && ctx.me.isAdminRole && <MenuItem onClick={() => { setCancelling(menu.sprint); setMenu(null); }}>Cancel sprint</MenuItem>}
        {can("sprint.delete") && menu?.sprint.status === "future" && <MenuItem sx={{ color: "#B42318" }} onClick={async () => { const s = menu.sprint; setMenu(null); if (!window.confirm(`Delete ${s.name}? Its ${menu.count} issue(s) go back to the backlog.`)) return; try { await pm.deleteSprint(s._id); load(); } catch (err) { ctx.fail(err); } }}>Delete sprint</MenuItem>}
      </Menu>
      <SprintFormDialog state={sprintForm} board={board} project={project} onClose={() => setSprintForm(null)} onDone={() => { setSprintForm(null); load(); reloadBoard(); }} />
      {completing && <CompleteSprintDialog sprint={completing} onClose={() => setCompleting(null)} onDone={() => { setCompleting(null); load(); reloadBoard(); }} />}
      {cancelling && <CancelSprintDialog sprint={cancelling} onClose={() => setCancelling(null)} onDone={() => { setCancelling(null); load(); reloadBoard(); }} />}
      <IssueDetailModal
        issueKey={openKey}
        open={Boolean(openKey)}
        onClose={closeIssue}
        onOpenIssue={(key) => openIssue(key)}
        onChanged={load}
      />
    </Box>
  );
};

export default BoardBacklog;
